import React, { createContext, useCallback, useContext } from 'react';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';

import { PageErrorUi } from 'components';
import { useForm, useMutation, useSeriesFetch, useSegment } from 'utils/hooks';
import { SHIPPING_METHODS } from 'dictionary';
import { FORM_SCHEMA, DEFAULT_FORM_DATA } from './stubs';
import { createFormData, createPayload } from './utils';

/**
 * @type {React.Context<{
 *  data: import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>,
 *  errors: Partial<import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>>,
 *  isDirty: boolean,
 *  isLoading: boolean,
 *  isProcessing: boolean,
 *  onValidate: (updates: Partial<import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>>) => void,
 *  onChange: (updates: Partial<import("zod").z.infer<typeof import('./stubs').FORM_SCHEMA>>) => void,
 *  onSave: () => Promise<any>
 * }>}
 */
const ShippingConfigContext = createContext();

export function useShippingConfig() {
  const context = useContext(ShippingConfigContext);
  if (!context) {
    throw new Error(
      'useShippingConfig must be used within a ShippingConfigProvider'
    );
  }
  return context;
}

export function ShippingConfigProvider({ children = null }) {
  const { track } = useSegment();
  const {
    data,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const { isError, isLoading, fetchData: onReload } = useSeriesFetch(
    [
      {
        dataKey: 'sendLabelId',
        apiKey: 'configure.deductions.shipmentMethod',
        onTransform: data => {
          const methods = _isArray(data) ? data : [];
          const sendLabelData = methods.find(method => {
            const slug = _get(method, 'shippingMethod', '');
            return slug === SHIPPING_METHODS.SEND_LABEL;
          });
          return _get(sendLabelData, 'id', '');
        }
      },
      {
        dataKey: 'info',
        apiKey: 'configure.deductions.shipmentMethodDetails',
        onSkipRequest: data => !_get(data, 'sendLabelId', ''),
        payload: data => ({ routeParam: _get(data, 'sendLabelId', '') })
      }
    ],
    {
      onTransform: data => _get(data, 'info[0]', {}),
      onSuccess: data => onSetInitialData(createFormData(data))
    }
  );

  const { isProcessing, mutate } = useMutation(
    'configure.deductions.shipmentMethodDetails',
    'PUT',
    {
      routeParam: _get(data, 'id', ''),
      errorMessage: 'Failed to save shipping configuration.',
      payload: () => createPayload(data),
      onValidate: () => onValidate(data),
      onSuccess: responseData => {
        track('FTU Wizard Shipping Configured', {
          paid_by: data.paidBy,
          fee_type: data.feeType,
          fee_amount: data.feeAmount
        });
        onSetInitialData(createFormData(responseData));
      }
    }
  );

  const onSave = useCallback(() => {
    return new Promise((resolve, reject) => {
      mutate({}, (err, data) => {
        if (err) reject(err);
        else resolve(data);
      });
    });
  }, [mutate]);

  return (
    <ShippingConfigContext.Provider
      value={{
        data,
        errors,
        isDirty,
        isLoading,
        isProcessing,
        onValidate,
        onChange,
        onSave
      }}
    >
      <PageErrorUi isError={isError} onReload={onReload}>
        {children}
      </PageErrorUi>
    </ShippingConfigContext.Provider>
  );
}
